"use client";

import { NFONE_APP_STORE_URL } from "@newform/constants";
import { ArrowRight, Smartphone } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

/**
 * One line at the top of the sign-in page: the app exists, here is the way
 * to it. The whole pill is the link, so the target is the pill and not the
 * last two words; the arrow is what says "this goes somewhere".
 *
 * Safari on iOS already shows Apple's Smart App Banner above the page (the
 * `itunes` metadata on the layout), so this one earns its place in every
 * other browser, and on iOS it is the second mention, not the first.
 */
export function AppStoreBanner({ className }: { className?: string }) {
  const t = useTranslations("signIn.appStore");
  return (
    <a
      href={NFONE_APP_STORE_URL}
      target="_blank"
      rel="noopener"
      className={cn(
        "group inline-flex max-w-full items-center gap-nf-ds-2 rounded-nf-ds-pill",
        "border border-nf-ds-border bg-nf-ds-bg-surface py-nf-ds-2 pr-nf-ds-3 pl-nf-ds-4",
        "font-nf-ds-sans text-nf-ds-sm font-medium text-nf-ds-fg shadow-nf-ds-xs outline-hidden",
        "transition-[background-color,opacity] duration-150 ease-nf-ds-out",
        "hover:bg-nf-ds-bg-muted active:opacity-70",
        "focus-visible:ring-2 focus-visible:ring-nf-ds-brand",
        "motion-reduce:transition-none",
        className,
      )}
    >
      <Smartphone
        aria-hidden="true"
        className="size-4 shrink-0 text-nf-ds-brand"
      />
      <span className="min-w-0 text-pretty">
        <span className="text-nf-ds-fg-muted">{t("bannerText")}</span>{" "}
        {t("bannerAction")}
      </span>
      <ArrowRight
        aria-hidden="true"
        className="size-4 shrink-0 transition-transform duration-150 ease-nf-ds-out group-hover:translate-x-0.5 motion-reduce:transition-none"
      />
    </a>
  );
}
